import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { Search, Menu, X, User, LogOut, Library, LayoutDashboard, Heart, Gift, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

export default function Header() {
  const { user, isAdmin, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(query)}`);
    setMobileOpen(false);
  };

  const navLinks = [
    { to: "/", label: t("nav_home") },
    { to: "/browse", label: t("nav_browse") },
  ];
  if (user) navLinks.push({ to: "/library", label: t("nav_library") });

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-4">
          <Logo size="md" />

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(l.to) ? "text-brand-400" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isActive("/admin") ? "text-brand-400" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <LayoutDashboard className="w-4 h-4" /> {t("nav_admin")}
              </Link>
            )}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md ml-auto">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search_placeholder")}
                className="w-full h-10 pl-10 pr-4 rounded-full bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
              />
            </div>
          </form>

          <div className="flex items-center gap-2 ml-auto md:ml-0">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 h-9 pl-1 pr-3 rounded-full border border-border hover:border-brand-500/50 transition-colors">
                    <span className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-brand-950 text-xs font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">{user.name.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/library")}>
                    <Library className="w-4 h-4 mr-2" /> {t("nav_library")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")}>
                    <Heart className="w-4 h-4 mr-2" /> {t("wishlist")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/redeem")}>
                    <Gift className="w-4 h-4 mr-2" /> {t("redeem")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    <Settings className="w-4 h-4 mr-2" /> Account
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> {t("nav_admin")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-400 focus:text-red-400">
                    <LogOut className="w-4 h-4 mr-2" /> {t("signout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" className="hidden sm:inline-flex text-sm" onClick={() => navigate("/login")}>
                  {t("signin")}
                </Button>
                <Button
                  className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold text-sm rounded-full px-5"
                  onClick={() => navigate("/register")}
                >
                  {t("signup")}
                </Button>
              </>
            )}

            <button className="lg:hidden p-2 -mr-2" onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-3 animate-float-up">
          <form onSubmit={submitSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full h-11 pl-10 pr-4 rounded-full bg-secondary border border-border text-sm outline-none"
            />
          </form>
          <nav className="flex flex-col">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium">
                {l.label}
              </Link>
            ))}
            {isAdmin && (
              <Link to="/admin" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium">
                {t("nav_admin")}
              </Link>
            )}
            {/* Below 640px the "Sign in" button in the bar is hidden, and this
                menu had no auth entry either — a returning listener on a phone
                could only see "Sign up". */}
            {!user && (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium text-brand-400">
                {t("signin")}
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
